import {
  type FormEvent,
  type JSX,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { helpTopicPath, routes } from '../../routes';
import { SessionApiError } from '../../session/client';
import { BackLink, type GateCopy, SignInGate } from '../panels';
import {
  type CourierMessage,
  type CourierView,
  ORDERS_LIMIT,
  type Order,
  type Refund,
  type StorefrontPlace,
  type Tracking,
  cancelOrder,
  getCourier,
  getHandover,
  getMessages,
  getRefunds,
  getStorefrontPlace,
  getTracking,
  listOrders,
  sendMessage,
} from './orders-api';
import {
  type Step,
  activeCourier,
  badgeOf,
  cancellable,
  completedRefundMinor,
  day,
  heroOf,
  isDelivery,
  isFinal,
  itemCount,
  money,
  paymentWords,
  primaryOf,
  reachedAt,
  shopOf,
  stepsOf,
  stripOf,
  time,
  tintFor,
  vehicleWords,
  wasPaid,
} from './order-model';
import './orders.css';

/**
 * The Orders page — the `Just Treated Orders` frame, over the platform's own
 * orders.
 *
 * Three views, as the frame has them: the logged-out panel with this browser's
 * guest orders, the signed-in list (Active and Past), and one order's detail
 * (`?order=<id>`, the frame's own address for it). The detail works for a
 * guest order too, because the frame lets a guest open one.
 *
 * Where the frame draws something the platform does not have, the element
 * keeps its place and says "Coming soon", or is left out and listed in the
 * hand-off — never the frame's demo: no Sam, no ETA, no moving dot, no Visa
 * ···· 4218, no hash-made collection code, no service fee, no stars that save
 * nothing.
 */

const IC = {
  truck: 'M3 16V8h11v8M14 11h4l3 3v2h-7M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  store: 'M3 10h18l-1.5-5h-15L3 10ZM5 10v10h14V10M10 20v-5h4v5',
  card: 'M3.5 6.5h17v11h-17zM3.5 10h17M7 14.5h4',
  warn: 'M12 3.5 2.5 20h19ZM12 10v4.5M12 17.2h.01',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5M12 16.5h.01',
  redo: 'M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4',
  file: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6',
  x: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9 9l6 6M15 9l-6 6',
  check: 'm5 12.5 4.2 4.2L19 7',
  rx: 'M9 3h6v4H9zM6 5h12v16H6zM9 12h6M12 9v6',
  bag: 'M5 8h14l-1 12H6ZM9 8V6a3 3 0 0 1 6 0v2',
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
  chat: 'M4 5h16v11H8l-4 4Z',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z',
  star: 'M12 3.5 14.6 9l6 .6-4.5 4 1.3 5.9L12 16.6 6.6 19.5 7.9 13.6 3.4 9.6l6-.6Z',
} as const;

function Icon({
  d,
  stroke = 'currentColor',
  size = 17,
  width = 2,
}: {
  readonly d: string;
  readonly stroke?: string;
  readonly size?: number;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Soon(): JSX.Element {
  return <span className="jt-ord-soon">Coming soon</span>;
}

function messageOf(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

export const orderHref = (orderId: string): string =>
  `${routes.orders.path}?order=${encodeURIComponent(orderId)}`;

interface Loaded<T> {
  readonly value?: T;
  readonly error?: SessionApiError | Error;
  readonly loading: boolean;
  reload(quiet?: boolean): void;
}

/** One record, loaded once and reloadable; a quiet reload keeps what is on screen while it asks. */
function useLoaded<T>(load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<{ value?: T; error?: SessionApiError | Error; loading: boolean }>({
    loading: true,
  });
  const loadRef = useRef(load);
  loadRef.current = load;
  const alive = useRef(true);

  const reload = useCallback((quiet = false) => {
    if (!quiet) setState((previous) => ({ ...previous, loading: true }));
    loadRef
      .current()
      .then((value) => {
        if (alive.current) setState({ value, loading: false });
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        const failure = error instanceof Error ? error : new Error('Something went wrong.');
        // A quiet poll that fails keeps the last good answer on screen.
        setState((previous) =>
          quiet && previous.value !== undefined ? previous : { error: failure, loading: false },
        );
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  return { ...state, reload };
}

function useNarrow(): boolean {
  const read = (): boolean => (typeof window === 'undefined' ? false : window.innerWidth < 940);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = (): void => setNarrow(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function useToast(): [string, (message: string) => void] {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, flash];
}

function Toast({ message }: { readonly message: string }): JSX.Element | null {
  return message === '' ? null : (
    <div className="jt-ord-toast" role="status">
      {message}
    </div>
  );
}

// -- Page ------------------------------------------------------------------------

export function OrdersPage({ signedIn }: { readonly signedIn: boolean }): JSX.Element {
  const navigator = useNavigator();
  const selected = navigator.location.query.get('order');

  if (selected !== null && selected !== '') {
    return <OrderDetail key={`${selected}:${String(signedIn)}`} orderId={selected} canBack={signedIn} />;
  }
  return signedIn ? <OrdersList /> : <OrdersGate />;
}

// -- Logged out --------------------------------------------------------------------

/** The frame's own logged-out copy (`Just Treated Orders`, the gate). */
const ORDERS_GATE: GateCopy = {
  head: "Everything you've ordered",
  copy: 'Log in to track deliveries live, reorder past items and get help with an order.',
};

/** The frame's gate, plus "Guest orders on this device" when this browser has placed any. */
function OrdersGate(): JSX.Element {
  const orders = useLoaded(listOrders);
  const guest = orders.value ?? [];

  return (
    <>
      <BackLink />
      <SignInGate title="Orders" gate={ORDERS_GATE} />
      {guest.length > 0 ? (
        <div className="jt-ord-guest">
          <span className="jt-ord-guest__head">Guest orders on this device</span>
          {guest.map((order) => {
            const shop = shopOf(order);
            const badge = badgeOf(order);
            return (
              <AppLink key={order.id} to={orderHref(order.id)} className="jt-ord-guest__row">
                <span
                  className="jt-ord-tile jt-ord-tile--42"
                  style={{ background: shop.tile }}
                  aria-hidden="true"
                >
                  {shop.initials}
                </span>
                <span className="jt-ord-guest__text">
                  <span className="jt-ord-guest__store">{shop.name}</span>
                  <span className="jt-ord-guest__meta">
                    {order.reference} · {day(order.placedAt)} · {money(order.totalMinor, order.currency)}
                  </span>
                </span>
                <span className="jt-ord-badge" style={{ background: badge.bg, color: badge.fg }}>
                  {badge.label}
                </span>
              </AppLink>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

// -- Signed in: the list -------------------------------------------------------------

function OrdersList(): JSX.Element {
  const navigator = useNavigator();
  const orders = useLoaded(listOrders);
  const [tab, setTab] = useState<'active' | 'past'>('active');

  const all = orders.value ?? [];
  const active = all.filter((o) => !isFinal(o));
  const past = all.filter((o) => isFinal(o));
  const list = tab === 'past' ? past : active;
  const capped = all.length >= ORDERS_LIMIT;

  const open = (order: Order, event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    navigator.navigate(orderHref(order.id));
  };

  let body: ReactNode;
  if (orders.value === undefined && orders.loading) {
    body = (
      <div className="jt-ord-panel" aria-busy="true">
        <span className="jt-ord-panel__copy">Loading your orders…</span>
      </div>
    );
  } else if (orders.value === undefined) {
    body = (
      <div className="jt-ord-panel" role="alert">
        <span className="jt-ord-panel__head">We couldn’t load your orders</span>
        <span className="jt-ord-panel__copy">{messageOf(orders.error)}</span>
        <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={() => orders.reload()}>
          Try again
        </button>
      </div>
    );
  } else if (list.length === 0) {
    body = (
      <div className="jt-ord-empty">
        <span className="jt-ord-empty__icon" aria-hidden="true">
          <Icon d={IC.bag} stroke="#5b2ede" size={26} />
        </span>
        <span className="jt-ord-empty__title">
          {tab === 'past' ? 'No past orders yet' : 'No active orders'}
        </span>
        <span className="jt-ord-empty__sub">
          {tab === 'past'
            ? "Orders you've received or collected will show here."
            : 'When you place an order, you can track it here.'}
        </span>
        {routes.nearby.availability === 'available' ? (
          <AppLink to={routes.nearby.path} className="jt-ord-pill jt-ord-pill--ink">
            Browse places near you
          </AppLink>
        ) : (
          <span className="jt-ord-pill jt-ord-pill--ink jt-ord-pill--off" aria-disabled="true">
            Browse places near you <Soon />
          </span>
        )}
      </div>
    );
  } else {
    body = (
      <div className="jt-ord-cards">
        {list.map((order) => (
          <OrderCard key={order.id} order={order} onOpen={open} />
        ))}
      </div>
    );
  }

  return (
    <>
      <BackLink />
      <div className="jt-ord-listhead">
        <div className="jt-ord-listhead__text">
          <h1 className="jt-ord-h1">Orders</h1>
          <span className="jt-ord-listhead__sub">Track, reorder and get help with your orders</span>
        </div>
        <div role="tablist" aria-label="Order status" className="jt-ord-tabs">
          {(
            [
              ['active', `Active · ${String(active.length)}`],
              ['past', `Past · ${String(past.length)}`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`jt-ord-tab${tab === id ? ' jt-ord-tab--on' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {body}
      {capped && orders.value !== undefined ? (
        <p className="jt-ord-note">Showing your {String(ORDERS_LIMIT)} most recent orders.</p>
      ) : null}
    </>
  );
}

const STRIP_ICON = {
  truck: IC.truck,
  store: IC.store,
  card: IC.card,
  warn: IC.warn,
  check: IC.check,
} as const;

function OrderCard({
  order,
  onOpen,
}: {
  readonly order: Order;
  readonly onOpen: (order: Order, event: MouseEvent<HTMLAnchorElement>) => void;
}): JSX.Element {
  const shop = shopOf(order);
  const badge = badgeOf(order);
  const strip = stripOf(order);
  const count = itemCount(order);
  const shown = order.lines.slice(0, 3);
  const more = order.lines.length > 3 ? order.lines.length - 2 : 0;
  const secondary = isFinal(order) && order.payment.status !== 'REFUNDED' ? 'Reorder' : 'Get help';

  return (
    <article className="jt-ord-card">
      <div className="jt-ord-card__top">
        <span className="jt-ord-tile jt-ord-tile--46" style={{ background: shop.tile }} aria-hidden="true">
          {shop.initials}
        </span>
        <span className="jt-ord-card__who">
          <span className="jt-ord-card__store">{shop.name}</span>
          <span className="jt-ord-card__meta">
            {order.reference} · {day(order.placedAt)}, {time(order.placedAt)}
          </span>
        </span>
        <span className="jt-ord-badge jt-ord-badge--card" style={{ background: badge.bg, color: badge.fg }}>
          {badge.label}
        </span>
      </div>
      <div className="jt-ord-card__mid">
        <span className="jt-ord-thumbs">
          {shown.map((line, index) =>
            more > 0 && index === 2 ? (
              <span key="more" role="img" aria-label="More items" className="jt-ord-thumb jt-ord-thumb--more">
                +{String(more)}
              </span>
            ) : (
              <span
                key={line.id}
                role="img"
                aria-label={line.title}
                className="jt-ord-thumb"
                style={{
                  background: `${tintFor(line.title)}${line.imageUrl !== undefined ? ` url(${JSON.stringify(line.imageUrl)}) center / cover no-repeat` : ''}`,
                }}
              />
            ),
          )}
        </span>
        <span className="jt-ord-card__sum">
          <span className="jt-ord-card__total">{money(order.totalMinor, order.currency)}</span>
          <span className="jt-ord-card__count">
            {String(count)} {count === 1 ? 'item' : 'items'} · {isDelivery(order) ? 'Delivery' : 'Collection'}
          </span>
        </span>
      </div>
      {strip !== undefined ? (
        <div className="jt-ord-strip" style={{ background: strip.bg, color: strip.fg }}>
          <Icon d={STRIP_ICON[strip.icon]} />
          <span>{strip.text}</span>
        </div>
      ) : null}
      <div className="jt-ord-card__actions">
        <a
          href={orderHref(order.id)}
          className="jt-ord-pill jt-ord-pill--ink"
          onClick={(e) => onOpen(order, e)}
        >
          {primaryOf(order)}
        </a>
        {secondary === 'Get help' ? (
          // Stage 11: the Help page is built, so "Get help" opens it.
          <AppLink to={helpTopicPath('orders')} className="jt-ord-pill jt-ord-pill--soft">
            {secondary}
          </AppLink>
        ) : (
          <span className="jt-ord-pill jt-ord-pill--soft jt-ord-pill--off" aria-disabled="true">
            {secondary} <Soon />
          </span>
        )}
      </div>
    </article>
  );
}

// -- One order -------------------------------------------------------------------------

type ModalKind = 'cancel' | 'nocancel' | 'messages';

interface Extras {
  readonly refunds: readonly Refund[];
  readonly courier?: CourierView;
  readonly handover?: string;
  readonly place?: StorefrontPlace;
}

const LIVE_DELIVERY = ['CONFIRMED', 'PREPARING', 'OUT_FOR_DELIVERY'];
const POLL_MS = 15_000;

/** What the detail shows besides the order itself. Each part failing on its own leaves the rest. */
async function loadExtras(order: Order): Promise<Extras> {
  const delivery = isDelivery(order);
  const [refunds, courier, handover, place] = await Promise.all([
    getRefunds(order.id)
      .then((r) => r.refunds)
      .catch(() => [] as readonly Refund[]),
    delivery && order.status !== 'PLACED' && order.status !== 'CANCELLED'
      ? getCourier(order.id).catch(() => undefined)
      : Promise.resolve(undefined),
    delivery && LIVE_DELIVERY.includes(order.status)
      ? getHandover(order.id)
          .then((h) => h.collection?.code)
          .catch(() => undefined)
      : Promise.resolve(undefined),
    !delivery && order.status === 'READY_FOR_COLLECTION' && order.storefront !== undefined
      ? getStorefrontPlace(order.storefront.slug).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);
  return {
    refunds,
    ...(courier !== undefined ? { courier } : {}),
    ...(handover !== undefined ? { handover } : {}),
    ...(place !== undefined ? { place } : {}),
  };
}

function directionsUrl(place: StorefrontPlace | undefined, fallbackName: string): string | undefined {
  const where = place?.locations.find((l) => l.postcode !== undefined || l.town !== undefined);
  if (where === undefined) return undefined;
  const query = [place?.name ?? fallbackName, where.town, where.postcode]
    .filter((p) => p !== undefined)
    .join(' ');
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;
}

function OrderDetail({
  orderId,
  canBack,
}: {
  readonly orderId: string;
  readonly canBack: boolean;
}): JSX.Element {
  const navigator = useNavigator();
  const narrow = useNarrow();
  const [toast, flash] = useToast();
  const [modal, setModal] = useState<ModalKind | undefined>();
  const [refusal, setRefusal] = useState<string | undefined>();

  const tracking = useLoaded<{ track: Tracking; extras: Extras }>(async () => {
    const track = await getTracking(orderId);
    return { track, extras: await loadExtras(track.order) };
  });

  const order = tracking.value?.track.order;
  const finished = tracking.value?.track.finished ?? true;

  // Live: while the order is still moving, ask again every fifteen seconds.
  const reloadTracking = tracking.reload;
  useEffect(() => {
    if (finished) return undefined;
    const timer = setInterval(() => reloadTracking(true), POLL_MS);
    return () => clearInterval(timer);
  }, [finished, reloadTracking]);

  const backToList = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    navigator.navigate(routes.orders.path);
  };

  const back = canBack ? (
    <a href={routes.orders.path} className="jt-site-back" onClick={backToList}>
      <Icon d="M14.6 5.4 8 12l6.6 6.6" size={15} width={2.6} />
      All orders
    </a>
  ) : null;

  if (order === undefined) {
    const missing = tracking.error instanceof SessionApiError && tracking.error.status === 404;
    return (
      <>
        {back}
        {tracking.loading ? (
          <div className="jt-ord-panel" aria-busy="true">
            <span className="jt-ord-panel__copy">Loading your order…</span>
          </div>
        ) : (
          <div className="jt-ord-panel" role="alert">
            <span className="jt-ord-panel__head">
              {missing ? 'We couldn’t find this order' : 'We couldn’t load this order'}
            </span>
            <span className="jt-ord-panel__copy">
              {missing
                ? canBack
                  ? 'It isn’t one of your orders, or the link is wrong.'
                  : 'It wasn’t placed on this device. Log in to see the orders on your account.'
                : messageOf(tracking.error)}
            </span>
            {missing ? null : (
              <button
                type="button"
                className="jt-ord-pill jt-ord-pill--soft"
                onClick={() => tracking.reload()}
              >
                Try again
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  const extras = tracking.value?.extras ?? { refunds: [] };
  const journey = tracking.value?.track.journey ?? [];
  const shop = shopOf(order);
  const badge = badgeOf(order);
  const hero = heroOf(order, extras.refunds);
  const steps = stepsOf(order, journey);
  const delivery = isDelivery(order);
  const cancelled = order.status === 'CANCELLED';
  const live = !isFinal(order);
  const courier = activeCourier(extras.courier);
  const refunded = completedRefundMinor(extras.refunds);
  const ready = order.status === 'READY_FOR_COLLECTION';
  const delivered = order.status === 'COMPLETED' && delivery;
  const maps = ready ? directionsUrl(extras.place, shop.name) : undefined;

  const doneAt = reachedAt(order, 'COMPLETED');
  const handedTo =
    extras.courier?.recipientName !== undefined &&
    extras.courier.recipientName !== null &&
    extras.courier.recipientName !== ''
      ? extras.courier.recipientName.split(' ')[0]
      : 'you';
  const handedAt = extras.courier?.deliveredAt ?? doneAt;
  const whereText = delivery
    ? order.deliveryAddress !== undefined
      ? [
          order.deliveryAddress.line1,
          order.deliveryAddress.line2,
          `${order.deliveryAddress.town} ${order.deliveryAddress.postcode}`,
        ]
          .filter((p) => p !== undefined && p !== '')
          .join(', ')
      : ''
    : shop.name;

  const lines: { k: string; v: string; fg?: string }[] = [
    { k: 'Subtotal', v: money(order.itemsTotalMinor, order.currency) },
  ];
  if (delivery) lines.push({ k: 'Delivery fee', v: money(order.deliveryFeeMinor, order.currency) });
  const penny = order.rewards?.redemption?.discountMinor ?? 0;
  if (penny > 0) lines.push({ k: 'PENNY', v: `−${money(penny, order.currency)}`, fg: '#156b49' });
  if (refunded > 0) lines.push({ k: 'Refunded', v: `−${money(refunded, order.currency)}`, fg: '#156b49' });

  const proposed = new Map(
    (order.substitutions ?? [])
      .filter((s) => s.status === 'PROPOSED')
      .map((s) => [s.lineId, s.replacementTitle]),
  );

  const refundPanel = ((): { title: string; note: string; bg: string; fg: string } | undefined => {
    if (extras.refunds.length > 0) {
      const latest = extras.refunds[extras.refunds.length - 1];
      const allDone = extras.refunds.every((r) => r.status === 'COMPLETED');
      const failed = extras.refunds.some((r) => r.status === 'FAILED');
      return {
        title: failed ? 'A refund did not go through' : allDone ? 'Refund complete' : 'Refund in progress',
        note:
          latest === undefined
            ? ''
            : `${money(latest.amountMinor, latest.currency)} ${
                latest.status === 'COMPLETED'
                  ? `was refunded on ${day(latest.completedAt ?? latest.requestedAt)}`
                  : latest.status === 'FAILED'
                    ? 'could not be refunded. The pharmacy has been told.'
                    : `is being refunded. It was started on ${day(latest.requestedAt)}.`
              }`,
        bg: failed ? '#fbe3e3' : '#ddf2e7',
        fg: failed ? '#8c1919' : '#0b5a4a',
      };
    }
    if (!cancelled) return undefined;
    return wasPaid(order)
      ? {
          title: 'Refund not recorded yet',
          note: 'You paid for this order. Returning the money is the pharmacy’s to do, and the refund shows here once it has been made.',
          bg: '#fbe3e3',
          fg: '#8c1919',
        }
      : {
          title: 'Nothing was charged',
          note: 'No payment was taken for this order.',
          bg: '#fbe3e3',
          fg: '#8c1919',
        };
  })();

  interface Action {
    readonly label: string;
    readonly icon: string;
    readonly danger?: boolean;
    readonly act?: () => void;
  }
  const actions: Action[] = [];
  // Stage 6: an order placed but not yet paid can be paid from the Checkout
  // page, which waits for the gateway's webhook like any payment there.
  if (
    order.status === 'PLACED' &&
    (order.payment.status === 'PENDING' || order.payment.status === 'FAILED')
  ) {
    actions.push({
      label: 'Pay for this order',
      icon: IC.card,
      act: () => navigator.navigate(`${routes.checkout.path}?order=${encodeURIComponent(order.id)}`),
    });
  }
  if (!isFinal(order)) {
    actions.push({
      label: 'Get help with this order',
      icon: IC.help,
      act: () => navigator.navigate(helpTopicPath('orders')),
    });
    actions.push({
      label: 'Cancel order',
      icon: IC.x,
      danger: true,
      act: () => {
        setRefusal(undefined);
        setModal(cancellable(order) ? 'cancel' : 'nocancel');
      },
    });
  }
  if (isFinal(order) && order.payment.status !== 'REFUNDED')
    actions.push({ label: 'Reorder these items', icon: IC.redo });
  actions.push({
    label: 'Download receipt',
    icon: IC.file,
    act: () => {
      if (downloadReceipt(order, extras.refunds)) flash(`Receipt for ${order.reference} downloaded`);
      else flash('Your browser could not save the receipt');
    },
  });
  if (isFinal(order)) actions.push({ label: 'Report a problem', icon: IC.help });

  return (
    <>
      <div className={`jt-ord-detail${narrow ? ' jt-ord-detail--narrow' : ''}`}>
        <div className="jt-ord-detail__main">
          {back}
          <section aria-label="Order status" className="jt-ord-status">
            <div className="jt-ord-status__head">
              <span className="jt-ord-status__text">
                <span className="jt-ord-status__badge" style={{ color: badge.fg }}>
                  {badge.label}
                </span>
                <h1 className="jt-ord-status__hero">{hero.title}</h1>
                <span className="jt-ord-status__sub">{hero.sub}</span>
              </span>
              {live ? (
                <span className="jt-ord-live">
                  <span className="jt-ord-live__dot" />
                  Live
                </span>
              ) : null}
            </div>

            {live && !cancelled ? (
              <div
                className="jt-ord-bar"
                aria-hidden="true"
                style={{ gridTemplateColumns: `repeat(${String(steps.length)},minmax(0,1fr))` }}
              >
                {steps.map((step, index) => (
                  <span key={index} className={`jt-ord-bar__seg jt-ord-bar__seg--${step.state}`} />
                ))}
              </div>
            ) : null}

            {courier !== null ? (
              <div className="jt-ord-courier">
                <span className="jt-ord-courier__avatar" aria-hidden="true">
                  {courier.displayName
                    .split(/\s+/)
                    .map((w) => w.slice(0, 1))
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <span className="jt-ord-courier__text">
                  <span className="jt-ord-courier__name">
                    {extras.courier?.status === 'PICKED_UP'
                      ? `${courier.displayName} is delivering your order`
                      : `${courier.displayName} is collecting your order`}
                  </span>
                  <span className="jt-ord-courier__sub">
                    {[
                      vehicleWords(courier.vehicle),
                      extras.courier?.pickedUpAt !== undefined && extras.courier.pickedUpAt !== null
                        ? `picked up at ${time(extras.courier.pickedUpAt)}`
                        : undefined,
                    ]
                      .filter((p) => p !== undefined && p !== '')
                      .join(' · ')}
                  </span>
                </span>
                <span className="jt-ord-courier__btns">
                  <span
                    className="jt-ord-roundbtn jt-ord-roundbtn--off"
                    aria-disabled="true"
                    title="Calling is coming soon"
                  >
                    <Icon d={IC.phone} stroke="#8a7fb5" size={18} />
                    <span className="jt-ord-sr">Call {courier.displayName}: coming soon</span>
                  </span>
                  <button
                    type="button"
                    className="jt-ord-roundbtn"
                    aria-label={`Message ${courier.displayName}`}
                    onClick={() => setModal('messages')}
                  >
                    <Icon d={IC.chat} stroke="#17112b" size={18} />
                  </button>
                </span>
              </div>
            ) : null}

            {ready ? (
              <div className="jt-ord-code">
                <span className="jt-ord-code__text">
                  <span className="jt-ord-code__label">COLLECTION CODE</span>
                  <span className="jt-ord-code__value">{order.reference}</span>
                  <span className="jt-ord-code__hint">Show this at the counter.</span>
                </span>
                {maps !== undefined ? (
                  <a href={maps} target="_blank" rel="noopener noreferrer" className="jt-ord-code__btn">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
                      <circle cx="12" cy="10" r="2.4" />
                    </svg>
                    Directions
                  </a>
                ) : null}
              </div>
            ) : null}

            {extras.handover !== undefined ? (
              <div className="jt-ord-code">
                <span className="jt-ord-code__text">
                  <span className="jt-ord-code__label">HANDOVER CODE</span>
                  <span className="jt-ord-code__value">{extras.handover}</span>
                  <span className="jt-ord-code__hint">Give this to your courier at the door.</span>
                </span>
              </div>
            ) : null}

            {delivered ? (
              <div className="jt-ord-proof">
                <div className="jt-ord-proof__row">
                  <span className="jt-ord-proof__tick" aria-hidden="true">
                    <Icon d={IC.check} stroke="#156b49" size={18} width={2.6} />
                  </span>
                  <span className="jt-ord-proof__text">
                    <span className="jt-ord-proof__head">
                      {handedAt === undefined || handedAt === null
                        ? `Handed to ${handedTo}`
                        : `Handed to ${handedTo} at ${time(handedAt)}`}
                    </span>
                    <span className="jt-ord-proof__where">{whereText}</span>
                  </span>
                </div>
                <div className="jt-ord-proof__rate">
                  <span className="jt-ord-proof__ask">
                    How was your order? <Soon />
                  </span>
                  <span
                    role="radiogroup"
                    aria-label="Rate your order"
                    aria-disabled="true"
                    className="jt-ord-stars"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <span
                        key={n}
                        role="radio"
                        aria-checked="false"
                        aria-disabled="true"
                        aria-label={`${String(n)} ${n === 1 ? 'star' : 'stars'}`}
                        className="jt-ord-star"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#b8b0d1"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d={IC.star} />
                        </svg>
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            ) : null}

            {refundPanel !== undefined ? (
              <div className="jt-ord-refund" style={{ background: refundPanel.bg }}>
                <Icon d={IC.card} stroke={refundPanel.fg} size={18} width={2.2} />
                <span className="jt-ord-refund__text">
                  <span className="jt-ord-refund__title" style={{ color: refundPanel.fg }}>
                    {refundPanel.title}
                  </span>
                  <span className="jt-ord-refund__note">{refundPanel.note}</span>
                </span>
              </div>
            ) : null}

            <ol aria-label="Progress" className="jt-ord-steps">
              {steps.map((step, index) => (
                <StepRow key={index} step={step} last={index === steps.length - 1} cancelled={cancelled} />
              ))}
            </ol>
          </section>
        </div>

        <div className="jt-ord-detail__side">
          <aside aria-label="Order summary" className="jt-ord-summary">
            <div className="jt-ord-summary__head">
              <span
                className="jt-ord-tile jt-ord-tile--42"
                style={{ background: shop.tile }}
                aria-hidden="true"
              >
                {shop.initials}
              </span>
              <span className="jt-ord-summary__who">
                <span className="jt-ord-summary__store">{shop.name}</span>
                <span className="jt-ord-summary__meta">
                  {order.reference} · {day(order.placedAt)}, {time(order.placedAt)}
                </span>
              </span>
            </div>
            <div className="jt-ord-summary__items">
              {order.lines.map((line) => {
                const swap = proposed.get(line.id);
                const meta = [
                  `${money(line.unitPriceMinor, order.currency)} each`,
                  line.consultationId !== undefined ? 'Prescriber-reviewed' : undefined,
                  swap !== undefined ? `Replacement offered: ${swap}` : undefined,
                ]
                  .filter((p) => p !== undefined)
                  .join(' · ');
                return (
                  <div key={line.id} className="jt-ord-item">
                    <span
                      className="jt-ord-item__img"
                      style={{
                        background: `${tintFor(line.title)}${line.imageUrl !== undefined ? ` url(${JSON.stringify(line.imageUrl)}) center / cover no-repeat` : ''}`,
                      }}
                    >
                      <span className="jt-ord-item__qty">{String(line.quantity)}</span>
                    </span>
                    <span className="jt-ord-item__text">
                      <span className="jt-ord-item__name">{line.title}</span>
                      <span className="jt-ord-item__meta">{meta}</span>
                    </span>
                    <span className="jt-ord-item__total">{money(line.lineTotalMinor, order.currency)}</span>
                  </div>
                );
              })}
            </div>
            <div className="jt-ord-summary__money">
              {lines.map((line) => (
                <div key={line.k} className="jt-ord-line">
                  <span>{line.k}</span>
                  <span
                    className="jt-ord-line__v"
                    style={line.fg !== undefined ? { color: line.fg } : undefined}
                  >
                    {line.v}
                  </span>
                </div>
              ))}
              <div className="jt-ord-line jt-ord-line--total">
                <span>Total</span>
                <span>{money(order.totalMinor, order.currency)}</span>
              </div>
              <div className="jt-ord-facts">
                <span className="jt-ord-facts__k">Payment</span>
                <span className="jt-ord-facts__v">{paymentWords(order)}</span>
                <span className="jt-ord-facts__k">
                  {delivery ? (order.status === 'COMPLETED' ? 'Delivered to' : 'Deliver to') : 'Collect from'}
                </span>
                <span className="jt-ord-facts__v">{whereText}</span>
              </div>
            </div>
          </aside>
          <div className="jt-ord-actions">
            {actions.map((action) =>
              action.act !== undefined ? (
                <button
                  key={action.label}
                  type="button"
                  className={`jt-ord-action${action.danger === true ? ' jt-ord-action--danger' : ''}`}
                  onClick={action.act}
                >
                  <span className="jt-ord-action__icon">
                    <Icon d={action.icon} stroke={action.danger === true ? '#a81f1f' : '#5b2ede'} />
                  </span>
                  <span className="jt-ord-action__label">{action.label}</span>
                  <Icon d="M9.4 5.4 16 12l-6.6 6.6" stroke="#8a7fb5" size={14} width={2.6} />
                </button>
              ) : (
                <div key={action.label} className="jt-ord-action jt-ord-action--off" aria-disabled="true">
                  <span className="jt-ord-action__icon">
                    <Icon d={action.icon} stroke="#8a7fb5" />
                  </span>
                  <span className="jt-ord-action__label">{action.label}</span>
                  <Soon />
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      {modal === 'cancel' ? (
        <CancelModal
          order={order}
          onClose={() => setModal(undefined)}
          onCancelled={() => {
            setModal(undefined);
            flash('Order cancelled');
            tracking.reload(true);
          }}
          onRefused={(message) => {
            setRefusal(message);
            setModal('nocancel');
            tracking.reload(true);
          }}
        />
      ) : null}
      {modal === 'nocancel' ? (
        <NoCancelModal order={order} reason={refusal} onClose={() => setModal(undefined)} />
      ) : null}
      {modal === 'messages' && courier !== null ? (
        <MessagesModal orderId={order.id} name={courier.displayName} onClose={() => setModal(undefined)} />
      ) : null}
      <Toast message={toast} />
    </>
  );
}

function StepRow({
  step,
  last,
  cancelled,
}: {
  readonly step: Step;
  readonly last: boolean;
  readonly cancelled: boolean;
}): JSX.Element {
  const stopped = cancelled && step.state === 'current';
  return (
    <li
      className={`jt-ord-step jt-ord-step--${stopped ? 'cancelled' : step.state}${last ? ' jt-ord-step--last' : ''}`}
    >
      <span className="jt-ord-step__rail">
        <span className="jt-ord-step__dot">
          {step.state === 'done' ? <Icon d={IC.check} stroke="#fff" size={12} width={3.6} /> : null}
        </span>
        <span className="jt-ord-step__line" />
      </span>
      <span className="jt-ord-step__text">
        <span className="jt-ord-step__label">{step.label}</span>
        {step.sub !== '' ? <span className="jt-ord-step__sub">{step.sub}</span> : null}
      </span>
    </li>
  );
}

// -- Modals -------------------------------------------------------------------------------

function useEscape(onClose: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

function ModalFrame({
  title,
  copy,
  icon,
  iconBg,
  iconFg,
  children,
  onClose,
}: {
  readonly title: string;
  readonly copy?: string;
  readonly icon: string;
  readonly iconBg: string;
  readonly iconFg: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
}): JSX.Element {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, textarea')?.focus();
  }, []);
  return (
    <>
      <div className="jt-ord-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className="jt-ord-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jt-ord-m-title"
      >
        <div className="jt-ord-modal__head">
          <span className="jt-ord-modal__icon" style={{ background: iconBg }} aria-hidden="true">
            <Icon d={icon} stroke={iconFg} size={20} width={2.2} />
          </span>
          <span className="jt-ord-modal__text">
            <span id="jt-ord-m-title" className="jt-ord-modal__title">
              {title}
            </span>
            {copy !== undefined ? <span className="jt-ord-modal__copy">{copy}</span> : null}
          </span>
        </div>
        {children}
      </div>
    </>
  );
}

const REASONS = ['Ordered by mistake', 'Found it elsewhere', 'Taking too long', 'Something else'] as const;

function newKey(): string {
  try {
    return `web-cancel-${crypto.randomUUID()}`;
  } catch {
    return `web-cancel-${String(Date.now())}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function CancelModal({
  order,
  onClose,
  onCancelled,
  onRefused,
}: {
  readonly order: Order;
  readonly onClose: () => void;
  readonly onCancelled: () => void;
  readonly onRefused: (message: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  // One key per opening of the dialog: a retried press cannot cancel twice.
  const key = useRef(newKey());

  const go = (): void => {
    if (busy) return;
    setBusy(true);
    setFailure(undefined);
    cancelOrder(order.id, reason === '' ? undefined : reason, key.current)
      .then(() => onCancelled())
      .catch((error: unknown) => {
        if (error instanceof SessionApiError && error.status === 409) {
          onRefused(error.message);
          return;
        }
        setFailure(messageOf(error));
        setBusy(false);
      });
  };

  return (
    <ModalFrame
      title={`Cancel order ${order.reference}?`}
      copy={`It hasn't been prepared yet, so you can still cancel. ${
        wasPaid(order)
          ? 'You’ve paid for it: returning the money is the pharmacy’s to do, and the refund shows on this order once it has been made.'
          : 'Nothing has been taken for it yet, so there’s nothing to refund.'
      }`}
      icon={IC.warn}
      iconBg="#fdf2e4"
      iconFg="#b25e09"
      onClose={onClose}
    >
      <div className="jt-ord-reasons">
        <span className="jt-ord-reasons__head">
          Why are you cancelling? <span className="jt-ord-reasons__opt">(optional)</span>
        </span>
        <div className="jt-ord-reasons__list">
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={reason === r}
              className={`jt-ord-reason${reason === r ? ' jt-ord-reason--on' : ''}`}
              onClick={() => setReason(reason === r ? '' : r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="jt-ord-box">
        <span className="jt-ord-box__k">Order total</span>
        <b className="jt-ord-box__v">{money(order.totalMinor, order.currency)}</b>
      </div>
      {failure !== undefined ? (
        <p className="jt-ord-err" role="alert">
          {failure}
        </p>
      ) : null}
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Keep my order
        </button>
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--danger" onClick={go} disabled={busy}>
          {busy ? 'Cancelling…' : 'Cancel order'}
        </button>
      </div>
    </ModalFrame>
  );
}

function NoCancelModal({
  order,
  reason,
  onClose,
}: {
  readonly order: Order;
  readonly reason: string | undefined;
  readonly onClose: () => void;
}): JSX.Element {
  const shop = order.storefront?.name ?? 'The pharmacy';
  return (
    <ModalFrame
      title="This order can't be cancelled"
      copy={reason ?? `${shop} has already started preparing it, so it can no longer be cancelled here.`}
      icon={IC.rx}
      iconBg="#f0ecfa"
      iconFg="#5b2ede"
      onClose={onClose}
    >
      <div className="jt-ord-modal__actions">
        <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
          Back to order
        </button>
        <span className="jt-ord-mbtn jt-ord-mbtn--violet jt-ord-mbtn--off" aria-disabled="true">
          Contact support <Soon />
        </span>
      </div>
    </ModalFrame>
  );
}

/** "Message {courier}" — the platform's order chat, open while a courier holds the order. */
function MessagesModal({
  orderId,
  name,
  onClose,
}: {
  readonly orderId: string;
  readonly name: string;
  readonly onClose: () => void;
}): JSX.Element {
  const messages = useLoaded<readonly CourierMessage[]>(() => getMessages(orderId));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();

  const reloadMessages = messages.reload;
  useEffect(() => {
    const timer = setInterval(() => reloadMessages(true), POLL_MS);
    return () => clearInterval(timer);
  }, [reloadMessages]);

  const send = (event: FormEvent): void => {
    event.preventDefault();
    const body = draft.trim();
    if (body === '' || busy) return;
    setBusy(true);
    setFailure(undefined);
    sendMessage(orderId, body)
      .then(() => {
        setDraft('');
        setBusy(false);
        messages.reload(true);
      })
      .catch((error: unknown) => {
        setFailure(messageOf(error));
        setBusy(false);
      });
  };

  const list = messages.value ?? [];
  return (
    <ModalFrame title={`Message ${name}`} icon={IC.chat} iconBg="#f0ecfa" iconFg="#5b2ede" onClose={onClose}>
      <div className="jt-ord-chat" aria-live="polite">
        {messages.loading && messages.value === undefined ? (
          <span className="jt-ord-chat__empty">Loading messages…</span>
        ) : list.length === 0 ? (
          <span className="jt-ord-chat__empty">No messages yet.</span>
        ) : (
          list.map((m) => (
            <div key={m.id} className={`jt-ord-msg${m.from === 'CUSTOMER' ? ' jt-ord-msg--me' : ''}`}>
              <span className="jt-ord-msg__who">
                {m.from === 'CUSTOMER' ? 'You' : name} · {time(m.at)}
              </span>
              <span className="jt-ord-msg__body">{m.body}</span>
            </div>
          ))
        )}
      </div>
      <form className="jt-ord-chat__form" onSubmit={send}>
        <label htmlFor="jt-ord-chat-input" className="jt-ord-sr">
          Your message
        </label>
        <textarea
          id="jt-ord-chat-input"
          className="jt-ord-chat__input"
          value={draft}
          maxLength={2000}
          rows={2}
          placeholder={`Message ${name}`}
          onChange={(event) => setDraft(event.target.value)}
        />
        {failure !== undefined ? (
          <p className="jt-ord-err" role="alert">
            {failure}
          </p>
        ) : null}
        <div className="jt-ord-modal__actions">
          <button type="button" className="jt-ord-mbtn jt-ord-mbtn--soft" onClick={onClose}>
            Close
          </button>
          <button
            type="submit"
            className="jt-ord-mbtn jt-ord-mbtn--ink"
            disabled={busy || draft.trim() === ''}
          >
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

// -- Receipt ------------------------------------------------------------------------------

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

/**
 * The order's receipt as a file, built from the order's own record — the same
 * figures as the page, none recomputed. A receipt, not a VAT invoice: nothing
 * on the platform holds a VAT number or an invoice number, so none is printed.
 */
export function receiptHtml(order: Order, refunds: readonly Refund[]): string {
  const shop = order.storefront?.name ?? 'The pharmacy';
  const rows = order.lines
    .map(
      (line) =>
        `<tr><td>${escapeHtml(line.title)}</td><td>${String(line.quantity)}</td><td>${money(line.unitPriceMinor, order.currency)}</td><td>${money(line.lineTotalMinor, order.currency)}</td></tr>`,
    )
    .join('');
  const refunded = completedRefundMinor(refunds);
  const penny = order.rewards?.redemption?.discountMinor ?? 0;
  const extra = [
    isDelivery(order)
      ? `<tr><td colspan="3">Delivery fee</td><td>${money(order.deliveryFeeMinor, order.currency)}</td></tr>`
      : '',
    penny > 0 ? `<tr><td colspan="3">PENNY</td><td>−${money(penny, order.currency)}</td></tr>` : '',
  ].join('');
  return `<!doctype html><html lang="en-GB"><head><meta charset="utf-8"><title>Receipt ${escapeHtml(order.reference)} · Just Treated</title>
<style>body{font-family:system-ui,sans-serif;color:#17112b;max-width:640px;margin:32px auto;padding:0 16px}table{width:100%;border-collapse:collapse}td,th{text-align:left;padding:6px 0;border-bottom:1px solid #eee9f8}td:not(:first-child),th:not(:first-child){text-align:right}.muted{color:#55506b;font-size:13px}</style></head><body>
<h1>Receipt</h1>
<p><b>${escapeHtml(shop)}</b><br>Order ${escapeHtml(order.reference)} · ${escapeHtml(day(order.placedAt))}, ${escapeHtml(time(order.placedAt))}</p>
<table><thead><tr><th>Item</th><th>Qty</th><th>Each</th><th>Total</th></tr></thead><tbody>${rows}
<tr><td colspan="3">Subtotal</td><td>${money(order.itemsTotalMinor, order.currency)}</td></tr>${extra}
<tr><td colspan="3"><b>Total</b></td><td><b>${money(order.totalMinor, order.currency)}</b></td></tr>
${refunded > 0 ? `<tr><td colspan="3">Refunded</td><td>−${money(refunded, order.currency)}</td></tr>` : ''}</tbody></table>
<p>Payment: ${escapeHtml(paymentWords(order))}<br>${isDelivery(order) ? 'Delivery' : 'Collection'}</p>
<p class="muted">This is a receipt, not a VAT invoice.</p>
</body></html>`;
}

function downloadReceipt(order: Order, refunds: readonly Refund[]): boolean {
  try {
    const blob = new Blob([receiptHtml(order, refunds)], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Just-Treated-receipt-${order.reference}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}
